import {
  GROUP_RENDER_TYPES,
  TYPES,
} from 'src/components/ProntoHousingAdministration/QuestionMapping/constants';
import WebViewer from '@pdftron/webviewer';
import { initializeDocumentManager } from 'src/components/DocumenManagerV2/docManagerConfig';
import axios from 'src/utils/axios';
import {
  buildAnnotations,
  CUSTOM_DATA_KEY,
  parseJson,
} from 'src/components/ProntoHousingAdministration/QuestionMapping/utils';
import parseQueryParams from 'src/utils/query';
import { sortMappingTableCells } from 'src/components/ProntoHousingAdministration/QuestionMapping/Navigation/MappingTab/MappingTableConfigModal/MappingTableUtils';

export const CATEGORY_OPTIONS = {
  QUESTIONNAIRE: 'Questionnaire',
  ESIGN: 'eSign',
  MISCELLANEOUS: 'Miscellaneous',
};

export const ANNOTATION_TYPE = {
  SIT: 'sit',
  QIT: 'qit',
  MIT: 'mit',
  CALCULATION: 'calculation',
  MULTI_QITS: 'multi_qits',
  TABLE_CELL: 'table_cell',
  UNKNOWN: 'unknown',
};

export const MAPPING_TYPES = {
  question: {
    id: 'mappings',
  },
  mit: {
    id: 'mitMappings',
  },
  sit: {
    id: 'sitMappings',
  },
  question_choice: {
    id: 'mappings',
  },
};

export const VALID_MULTI_QIT_MAPPING_TYPES = [
  'text',
  'text_area',
  'calculation',
  'number',
  'zip_code',
  'ssn',
  'last_ssn',
  'name',
  'email',
  'phone',
  'account',
  'log_number',
  'currency',
  'date',
  'percentage',
  'choices',
  'bool',
  'multiselect',
  'frequency',
];

export const VALID_MAPPING_TABLE_QUESTION_TYPES = [
  'text',
  'text_area',
  'calculation',
  'number',
  'zip_code',
  'ssn',
  'last_ssn',
  'name',
  'email',
  'phone',
  'account',
  'log_number',
  'currency',
  'date',
  'percentage',
  'choices',
  'bool',
  'multiselect',
  'frequency',
];

export const getHouseholdMemberTypeData = (
  householdMemberType,
  multiGroup,
  createData
) => {
  const isDataValid =
    householdMemberType?.parent &&
    (householdMemberType?.options?.length > 0 || createData?.calculationId);
  if (
    isDataValid &&
    (householdMemberType?.renderType ||
      householdMemberType?.question_group_render_type_choice ||
      multiGroup?.question_group_render_type_choice)
  ) {
    return {
      household_member_type: householdMemberType?.options,
      question_group_render_type:
        householdMemberType.question_group_render_type ||
        GROUP_RENDER_TYPES.RENDER_ALL_ONE_PAGE.id,
      question_group_render_type_choice:
        householdMemberType?.renderType ||
        householdMemberType?.question_group_render_type_choice ||
        multiGroup?.question_group_render_type_choice ||
        undefined,
    };
  }
  return {};
};

export const initializeWebViewer = async (config, user, viewer) => {
  const instance = await WebViewer(config, viewer.current);
  initializeAnnotationTool(instance);
  initializeDocumentManager(instance, getDocumentManagerConfig(user));
  return instance;
};

const initializeAnnotationTool = (instance) => {
  const annotationTool = instance.Core.documentViewer.getTool(
    'AnnotationCreateFreeText'
  );
  annotationTool.disableAutoFocusOnCreate();
  return annotationTool;
};

const getDocumentManagerConfig = (user) => {
  return {
    user,
    isMappingTool: true,
    disabledFeatures: ['NotesPanel', 'ThumbnailMerging', 'ThumbnailReordering'],
    openLeftPanelOnMount: false,
    enableTooltipInfo: true,
  };
};

export const formMappingTableParams = {
  expand: [
    'question_groups',
    'table_cells',
    'table_cells.qits',
    'table_cells.qits.question_group',
    'table_cells.qits.question_choices',
    'table_cells.choices_custom_values',
    'table_cells.qits_choices',
    'table_cells.qits.question',
    'table_cells.calculation',
  ],
  fields: [
    'id',
    'row_overflow_threshold',
    'x',
    'y',
    'width',
    'height',
    'form_page',
    'question_groups.id',
    'question_groups.name',
    'member_types',
    'mapping_orientation',
    'rendering_mode',
    'layout_config',
    'table_cells.id',
    'table_cells.order',
    'table_cells.calculation_name',
    'table_cells.question_group_name',
    'table_cells.question_group_field',
    'table_cells.household_member_field',
    'table_cells.hhm_number',
    'table_cells.render_type',
    'table_cells.render_type_list',
    'table_cells.empty_render_value',
    'table_cells.x',
    'table_cells.y',
    'table_cells.width',
    'table_cells.height',
    'table_cells.mapping_table.id',
    'table_cells.calculation',
    'table_cells.qits.id',
    'table_cells.qits.question.name',
    'table_cells.qits.question.id',
    'table_cells.qits.question.question_type',
    'table_cells.qits.question_group.id',
    'table_cells.qits.question_group.name',
    'table_cells.qits.question_choices.id',
    'table_cells.qits.question_choices.name',
    'table_cells.choices_custom_values.id',
    'table_cells.choices_custom_values.qit',
    'table_cells.choices_custom_values.value',
    'table_cells.choices_custom_values.question_choice',
    'table_cells.qits_choices.id',
    'table_cells.qits_choices.qit',
    'table_cells.qits_choices.blank',
    'table_cells.qits_choices.choices',
    'table_cells.calculation.id',
    'table_cells.calculation.name',
    'table_cells.calculation.for_certification',
    'table_cells.calculation.member_types',
  ],
};

export const MappingTableCellQueryParams = {
  expand: [
    'qits',
    'qits.question_choices',
    'qits.question_group',
    'qits.question',
    'calculation',
    'choices_custom_values',
    'qits_choices',
  ],
  fields: [
    'id',
    'x',
    'y',
    'width',
    'height',
    'order',
    'calculation_name',
    'question_group_name',
    'question_group_field',
    'household_member_field',
    'hhm_number',
    'render_type',
    'render_type_list',
    'empty_render_value',
    'mapping_table.id',
    'calculation',
    'qits.id',
    'qits.question.name',
    'qits.question.id',
    'qits.question.question_type',
    'qits.question_group.id',
    'qits.question_group.name',
    'qits.question_choices.id',
    'qits.question_choices.name',
    'calculation.id',
    'calculation.name',
    'calculation.for_certification',
    'calculation.member_types',
    'choices_custom_values.id',
    'choices_custom_values.qit',
    'choices_custom_values.value',
    'choices_custom_values.question_choice',
    'qits_choices.id',
    'qits_choices.qit',
    'qits_choices.blank',
    'qits_choices.choices',
  ],
};

export const formatMappingTableCells = (formMappingTables, pages) => {
  return formMappingTables.flatMap((mapping_table) => {
    const { table_cells = [], question_groups = [], form_page } = mapping_table;

    return table_cells.sort(sortMappingTableCells).map((tableCell) => {
      const qits = [...(tableCell.qits || [])];
      if (qits.length > 0) {
        const qitQuestionGroups = qits.map((qit) => qit.question_group?.id);
        const skippedQuestionGroups = question_groups.filter(
          (group) => !qitQuestionGroups.includes(group.id)
        );

        skippedQuestionGroups.forEach(({ id, name }) => {
          qits.push({
            id: -1,
            question_group: { id, name },
            question: {
              id: -1,
              name: `Skip Question Group ${name}`,
            },
          });
        });
      }
      return {
        ...tableCell,
        qits,
        mapping_table_data: mapping_table,
        form_page: form_page,
        page: pages.findIndex((page) => page === form_page) + 1 || null,
      };
    });
  });
};

export const buildFinalAnnotations = async (
  instance,
  certificationConfigId,
  form,
  householdMemberTypes = [],
  pages
) => {
  let finalAnnotations = [];
  let qitAnnotations = await axios.get(
    `mapping_question/question_response_mapping/?form_page__form=${form}&expand=qit.question,qit.question_group,qit.section,qit.page,question_context.context_template`
  );
  let mitAnnotations = await axios.get(
    `mapping_question/content_mapping/?form_page__form=${form}&expand=mit`
  );
  let sitAnnotations = await axios.get(
    `signature/signature_mapping/?form_page__form=${form}&expand=sit`
  );
  let calculationAnnotations = await axios.get(
    `calculations/calculation_mapping/?form_page__form=${form}&expand=calculation,household_member_type`
  );
  const { data: formMappingTables } = await axios.get(
    `mapping_question/mapping_table/?form_page__form=${form}&${parseQueryParams(
      formMappingTableParams
    )}`
  );

  const mapAnnotation = (item) => ({
    ...item,
    page: pages.findIndex((page) => page === item.form_page) + 1 || null,
  });

  // raw data
  qitAnnotations = qitAnnotations.data.map((qit) => mapAnnotation(qit));
  mitAnnotations = mitAnnotations.data.map((mit) => mapAnnotation(mit));
  sitAnnotations = sitAnnotations.data.map((sit) => mapAnnotation(sit));
  calculationAnnotations = calculationAnnotations.data.map((calculation) =>
    mapAnnotation(calculation)
  );
  const tableCellMappings = formatMappingTableCells(formMappingTables, pages);

  finalAnnotations = [
    ...qitAnnotations,
    ...mitAnnotations,
    ...sitAnnotations,
    ...calculationAnnotations,
    ...tableCellMappings,
  ];

  buildAnnotations(
    instance,
    finalAnnotations,
    {
      NoRotate: false,
      NoResize: false,
      NoMove: false,
    },
    householdMemberTypes
  );

  instance.Core.documentViewer.refreshAll();
  instance.Core.documentViewer.updateView();

  return {
    qitAnnotations,
    mitAnnotations,
    sitAnnotations,
    calculationAnnotations,
    tableCellMappings,
    formMappingTables,
  };
};

export const setAnnotationsContent = (
  instance,
  annotation,
  annotationText,
  tooltipData
) => {
  const CustomData = parseJson(annotation.getCustomData(CUSTOM_DATA_KEY)) || {};
  const data = {
    ...CustomData,
    isProntoAnnotation: true,
  };
  if (tooltipData) {
    data.tooltipData = tooltipData;
  }
  annotation.setCustomData(CUSTOM_DATA_KEY, data);

  annotation.setContents(annotationText);
  instance.Core.annotationManager.selectAnnotation(annotation);
  annotation.LockedContents = true;
};

export const AddMappingErrorMessage = (page, data, selectedDocument) => {
  console.error({
    page: {
      value: page,
      error: !page ? 'Page not found.' : null,
    },
    document: {
      value: selectedDocument,
      error: !selectedDocument ? 'Document not found.' : null,
    },
    data: {
      value: data,
      error: !data ? 'Data not created.' : null,
    },
  });
};

export const validMappableItems = [
  TYPES.questionContext,
  TYPES.question,
  TYPES.questionChoice,
  TYPES.mit,
  TYPES.sit,
  TYPES.sitDate,
  TYPES.sitName,
  TYPES.sitHhmt,
  TYPES.sitPhone,
  TYPES.sitInitials,
  TYPES.calculation,
];
